import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import get from 'lodash/get';
import uniq from 'lodash/uniq';

import {
  AuthResponse,
  LocationDetails,
  PermittedLocation,
} from '@cfa/pathway-fe-shared/xp';

import { handleApiError } from '@/utils/api-error-handler';
import { isOktaTokenStorage } from '@/utils/auth';
import { permissionsAppliedToTokenAtom } from '@/utils/permissions-widget';
import {
  getFakeTokenHeader,
  getParsedImpersonatedToken,
  isImpersonating,
} from '@/utils/permissions-widget/token';
import { getUserInfoFromAuth } from '@/utils/user';

import Constants from '@/constants';

import { queryClient } from '@/services/api';
import { ConceptTypeLocations } from '@/services/content-api-types';
import { OktaUserRaw } from '@/types';

import { appInitializedAtom } from '@/app/state';
import { store } from '@/atoms';
import { env } from '@/env';
import oktaAuth from '@/okta/oktaConfig';

export const authQueryKey = ['auth'] as const;

const isOktaUserRaw = (claims: unknown): claims is OktaUserRaw => {
  if (typeof claims !== 'object' || claims === null) return false;
  const c = claims as Record<string, unknown>;
  return typeof c.email === 'string' && typeof c.userType === 'string';
};

const _fetchAuth = async (accessToken?: string) => {
  const webToken = oktaAuth.getAccessToken();
  const token =
    accessToken ??
    (window.native.isNative ? window.native.accessToken : webToken);
  if (!token) {
    throw new Error('No token');
  }

  const authState = oktaAuth.authStateManager.getAuthState();

  const impersonatedToken = getParsedImpersonatedToken();

  const parseStorage = (): OktaUserRaw | undefined => {
    try {
      const rawStorage = JSON.parse(
        localStorage.getItem('okta-token-storage') ?? 'null',
      ) as unknown;
      const storage = isOktaTokenStorage(rawStorage) ? rawStorage : undefined;
      const claims = storage?.accessToken?.claims;
      return isOktaUserRaw(claims) ? claims : undefined;
    } catch {
      return undefined;
    }
  };

  const webUser =
    env.REACT_APP_ENV !== 'prod' && impersonatedToken
      ? impersonatedToken
      : Constants.IS_IN_CYPRESS_TEST_LOCAL
        ? parseStorage()
        : (authState?.accessToken?.claims as unknown as
            | OktaUserRaw
            | undefined);

  const user = window.native.isNative ? window.native.nativeUser : webUser;
  if (!user) {
    throw new Error('No okta user found');
  }

  const responsePromise = fetch(`${Constants.XP_API_BASE_URL}/auth`, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...getFakeTokenHeader(),
    },
    credentials: 'include',
  });

  const licenseeStaffPromise = isLicenseeStaff(user)
    ? fetchLocationsForLicenseeStaff(token).catch((e: unknown) => {
        if (isImpersonating()) {
          handleApiError(e, 'fetchAuth');
          return [] as NonNullable<ConceptTypeLocations['locations']>;
        }
        throw e;
      })
    : undefined;

  const [resp, licenseeStaffLocations] = await Promise.all([
    responsePromise,
    licenseeStaffPromise,
  ]);

  if (!resp.ok) {
    throw new Error('Failed to get auth');
  }

  const response = (await resp.json()) as AuthResponse;

  const mappedLicenseeStaffLocations = licenseeStaffLocations?.map(
    location => ({
      ...location,
      hasInterimManager: false,
    }),
  ) as LocationDetails[] | undefined;

  return buildFullAuth(response, user, mappedLicenseeStaffLocations);
};

export const fetchAuth = (accessToken?: string) =>
  queryClient.fetchQuery({
    queryKey: authQueryKey,
    queryFn: () => _fetchAuth(accessToken),
    staleTime: 0,
  });

export const getAuth = () => queryClient.getQueryData<Auth>(authQueryKey);

type QueryOptions<TData = unknown, TSelect = unknown> = Omit<
  UseQueryOptions<TData, Error, TSelect>,
  'queryKey' | 'queryFn' | 'initialData'
>;

const isAuthQueryEnabledByDefault = (isAppInitialized: boolean): boolean =>
  window.native.isNative ||
  isAppInitialized ||
  Constants.IS_IN_JEST_TEST ||
  Constants.IS_IN_CYPRESS_TEST_LOCAL;

/** User auth. */
export const useAuth = <TSelect = Auth>(opts?: QueryOptions<Auth, TSelect>) => {
  const isAppInitialized = useAtomValue(appInitializedAtom);
  const enabledBySession = isAuthQueryEnabledByDefault(isAppInitialized);

  return useQuery({
    ...opts,
    queryKey: authQueryKey,
    queryFn: () => _fetchAuth(),
    enabled: enabledBySession && (opts?.enabled ?? true),
  });
};

export const buildFullAuth = (
  auth: AuthResponse,
  user: OktaUserRaw,
  licenseeStaffLocations?: LocationDetails[],
) => {
  const isInternationalUser =
    auth.user?.country === Constants.SUPPORTED_COUNTRIES.GB.id;

  const {
    audience,
    cdn,
    country,
    features,
    isLicensee,
    language,
    locations,
    locationsWithRoles,
    roles,
    selectedLocation,
    state,
    regions,
    markets,
    areaSupportTeams,
    operatorIds,
    userId,
  } = getUserInfoFromAuth(auth);

  const fullName = `${user.nickname} ${user.family_name}`;

  const licenseeLocations =
    licenseeStaffLocations ?? getLocations(auth.user?.locations, 'LIC');

  const franchiseeLocations = getLocations(auth.user?.locations, 'FRANCHISEE');

  const supportCenterLocations = getLocations(auth.user?.locations, 'CORP');

  const corpLocations = supportCenterLocations.filter(
    ({ entity }) => entity !== 'TG',
  );
  const tgLocations = supportCenterLocations
    .filter(({ entity }) => entity === 'TG')
    .map(({ name, operator }) => ({ name, id: operator?.id }));

  const locationsWithLogin = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.LOGIN,
  );

  const highestRoleLevel = getHighestRoleLevel(roles);

  const locationsWithOperatorRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.OPERATOR,
  );
  const locationsWithStakeholderRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.STAKEHOLDER,
  );
  const locationsWithLeaderRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.LEADER,
  );
  const locationsWithTrainerRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.TRAINER,
  );
  const locationsWithLoginRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.LOGIN,
  );
  const locationsWithAdminRole = getLocationsWithRoleLevel(
    roles,
    Constants.USER_ROLES.ADMIN,
  );

  return {
    audience,
    cdn,
    country,
    features,
    isLicensee,
    language,
    locations,
    locationsWithRoles,
    roles,
    selectedLocation,
    state,
    regions,
    markets,
    areaSupportTeams,
    operatorIds,
    userId,
    isInternationalUser,
    firstName: user.nickname,
    fullName,
    user: auth.user,
    userType: user.userType,
    email: user.email,
    isOperatorConsultant:
      (roles.OPERATOR?.length ?? 0) > 0 &&
      licenseeLocations.length > 0 &&
      audience === Constants.USER_AUDIENCE.OPERATOR,
    hasSupportCenterLocation: supportCenterLocations.length > 0,
    supportCenterbaseLocations: supportCenterLocations,
    supportCenterLocations: { corpLocations, tgLocations },
    userIsLicenseeStaff: !!licenseeStaffLocations,
    licenseeLocations: licenseeLocations,
    restrictions:
      country.id === Constants.SUPPORTED_COUNTRIES.GB.id
        ? [Constants.USER_RESTRICTIONS.GREAT_BRITAIN_USER]
        : [],
    isStaff: locationsWithLogin.includes(Constants.SUPPORT_STAFF_LOCATION),
    highestRoleLevel,
    isAdminOperatorOrStakeholder:
      highestRoleLevel === Constants.USER_ROLES.ADMIN ||
      highestRoleLevel === Constants.USER_ROLES.OPERATOR ||
      highestRoleLevel === Constants.USER_ROLES.STAKEHOLDER,
    isStakeholder: locationsWithStakeholderRole.length > 0,
    leaderCorpLocations: corpLocations.filter(
      corp =>
        corp.number &&
        (locationsWithLeaderRole.includes(corp.number) ||
          locationsWithStakeholderRole.includes(corp.number) ||
          locationsWithOperatorRole.includes(corp.number)),
    ),
    franchiseeLocations,
    locationsWithOperatorRole,
    locationsWithStakeholderRole,
    locationsWithLeaderRole,
    locationsWithTrainerRole,
    locationsWithLoginRole,
    locationsWithAdminRole,
    allLocationsWithAtLeastLeaderRoles: uniq([
      ...locationsWithLeaderRole,
      ...locationsWithStakeholderRole,
      ...locationsWithOperatorRole,
    ]),
    allLocationsWithAtLeastTrainer: uniq([
      ...locationsWithLeaderRole,
      ...locationsWithStakeholderRole,
      ...locationsWithOperatorRole,
      ...locationsWithTrainerRole,
    ]),
    assignableTrainerLocations: uniq([
      ...locationsWithLeaderRole,
      ...locationsWithStakeholderRole,
      ...locationsWithOperatorRole,
      ...locationsWithTrainerRole,
    ]).filter(loc => loc !== Constants.SUPPORT_STAFF_LOCATION),
    allLocationsWithAllRoles: uniq([
      ...locationsWithLeaderRole,
      ...locationsWithStakeholderRole,
      ...locationsWithOperatorRole,
      ...locationsWithTrainerRole,
      ...locationsWithLoginRole,
    ]),
  };
};

export type Auth = Awaited<ReturnType<typeof buildFullAuth>>;

const getLocationsWithRoleLevel = (
  userRoles: Record<string, string[]>,
  roleLevel: string,
): string[] => {
  const role = Object.keys(userRoles).find(r => r === roleLevel);
  const locations = get(userRoles, role ?? '');
  return Array.isArray(locations) && locations.length ? locations : [];
};

const getLocations = (
  locations: PermittedLocation[] | undefined,
  by: LocationDetails['businessModel'],
): LocationDetails[] =>
  (locations ?? []).reduce<LocationDetails[]>((acc, loc) => {
    const location = loc.location;
    if (location.businessModel === by) {
      return [...acc, location];
    }

    return acc;
  }, []);

const getHighestRoleLevel = (roles: Record<string, string[]>) => {
  if ((roles.ADMIN?.length ?? 0) > 0) {
    return Constants.USER_ROLES.ADMIN;
  } else if ((roles.OPERATOR?.length ?? 0) > 0) {
    return Constants.USER_ROLES.OPERATOR;
  } else if ((roles.LEADER?.length ?? 0) > 0) {
    return Constants.USER_ROLES.LEADER;
  } else if ((roles.STAKEHOLDER?.length ?? 0) > 0) {
    return Constants.USER_ROLES.STAKEHOLDER;
  } else if ((roles.TRAINER?.length ?? 0) > 0) {
    return Constants.USER_ROLES.TRAINER;
  }
  return Constants.USER_ROLES.LOGIN;
};

const fetchLocationsForLicenseeStaff = async (accessToken: string) => {
  const resp = await fetch(
    `${Constants.PATHWAY_API.BASE_URL}/locations?type=${Constants.LOCATION_TYPES.LIC}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...getFakeTokenHeader(),
      },
    },
  );

  if (!resp.ok) {
    // Include status info so handleApiError can detect 401/403 errors
    const error = Object.assign(
      new Error('Failed to get locations for licensee staff'),
      {
        status: resp.status,
        statusText: resp.statusText,
        url: resp.url,
      },
    );
    throw error;
  }

  const locations: ConceptTypeLocations[] =
    (await resp.json()) as ConceptTypeLocations[];

  return (
    locations.find(type => type.conceptType === Constants.LOCATION_TYPES.LIC)
      ?.locations ?? []
  );
};

const isLicenseeStaff = (user: OktaUserRaw) => {
  const appliedPerms = store.get(permissionsAppliedToTokenAtom);
  return (
    ((user.cfa_perms.P20.LICENSEESTAFF?.length ?? 0) > 0 ||
      appliedPerms.LICENSEESTAFF) ??
    false
  );
};

export default useAuth;
